import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExternalIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import cpeRequirementImportService, {
  type CpeRequirementImportDetail,
  type CpeRequirementImportStatus,
  type MonitorFinding,
  type StateRequirementConstraintSummary,
  type UpdateCpeRequirementFields,
} from '../../services/cpeRequirementImportService';
import { formatUtcDate } from '../../utils/dateTime';

/**
 * Verification goes stale when any import signal (scrape, AI parse, recorded
 * change) is newer than the human verification timestamp.
 */
function isVerificationStale(row: CpeRequirementImportStatus): boolean {
  if (!row.verifiedAt) return false;
  const verified = new Date(row.verifiedAt).getTime();
  return [row.lastScrapedAt, row.lastParsedAt, row.lastChangeAt].some(
    (t) => t != null && new Date(t).getTime() > verified,
  );
}

/** Timestamps (scrape/parse times) include the time of day, unlike UTC calendar dates. */
function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(value: string | null): string {
  return value ? formatUtcDate(value) : '—';
}

/** "TotalPeriod" -> "total period" for constraint scope display. */
function formatConstraintScope(scope: string): string {
  return scope.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

/** One-line summary of a flexible-model constraint; prefers its seeded description. */
function formatConstraint(con: StateRequirementConstraintSummary): string {
  if (con.description) return con.description;
  const parts: string[] = [];
  if (con.requiredHours != null) parts.push(`${con.requiredHours} hrs required`);
  if (con.minimumHours != null) parts.push(`${con.minimumHours} hrs minimum`);
  if (con.maximumHours != null) parts.push(`${con.maximumHours} hrs maximum`);
  if (con.frequencyMonths != null) parts.push(`every ${con.frequencyMonths} months`);
  if (con.appliesToNewLicensesOnly) parts.push('new licenses only');
  parts.push(formatConstraintScope(con.constraintScope));
  return parts.join(' · ');
}

type ImportStanding = 'imported' | 'partial' | 'never';

function getImportStanding(row: CpeRequirementImportStatus): ImportStanding {
  if (row.lastScrapedAt && row.lastParsedAt) return 'imported';
  if (row.lastScrapedAt || row.lastParsedAt) return 'partial';
  return 'never';
}

function ImportStandingChip({ standing }: { standing: ImportStanding }) {
  if (standing === 'imported') {
    return (
      <Chip
        label="Imported"
        size="small"
        sx={{ height: 22, bgcolor: 'status.completed.bg', color: 'status.completed.fg' }}
      />
    );
  }
  if (standing === 'partial') {
    return (
      <Chip
        label="Partial"
        size="small"
        sx={{ height: 22, bgcolor: 'status.modified.bg', color: 'status.modified.fg' }}
      />
    );
  }
  return (
    <Chip
      label="Never Imported"
      size="small"
      sx={{ height: 22, bgcolor: 'status.attention.bg', color: 'status.attention.fg' }}
    />
  );
}

/** Verification cell: green when verified, amber when import signals moved past the verification. */
function VerificationCellContent({ row }: { row: CpeRequirementImportStatus }) {
  if (!row.verifiedAt) {
    return <>—</>;
  }
  const stale = isVerificationStale(row);
  return (
    <>
      {stale ? (
        <Chip
          label="Changed since verified"
          size="small"
          sx={{ height: 22, bgcolor: 'status.modified.bg', color: 'status.modified.fg' }}
        />
      ) : (
        <Chip
          label="Verified"
          size="small"
          sx={{ height: 22, bgcolor: 'status.completed.bg', color: 'status.completed.fg' }}
        />
      )}
      <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
        {formatDateTime(row.verifiedAt)}
        {row.verifiedBy ? ` · ${row.verifiedBy}` : ''}
      </Typography>
    </>
  );
}

function StateBadge({ stateCode }: { stateCode: string }) {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '6px',
        bgcolor: 'brand.heroSurface',
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        flexShrink: 0,
      }}
    >
      {stateCode}
    </Box>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.5 }}>
      {children}
    </Typography>
  );
}

/**
 * Requirement changes reported by the external weekly NASBA monitor agent.
 * Unreviewed findings drive the notification badge on the admin dashboard;
 * marking a finding reviewed clears it from the default view.
 */
function MonitorFindingsSection({
  findings,
  lastRunAt,
  showReviewed,
  onToggleShowReviewed,
  reviewBusyId,
  onMarkReviewed,
  onStateClick,
}: {
  findings: MonitorFinding[];
  lastRunAt: string | null;
  showReviewed: boolean;
  onToggleShowReviewed: () => void;
  reviewBusyId: string | null;
  onMarkReviewed: (id: string) => void;
  onStateClick: (stateCode: string) => void;
}) {
  const unreviewed = findings.filter((f) => !f.reviewedAt);
  const visible = showReviewed ? findings : unreviewed;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <SectionHeading>
          Monitor Findings
          {unreviewed.length > 0 && ` · ${unreviewed.length} needing review`}
        </SectionHeading>
        <Button size="small" onClick={onToggleShowReviewed}>
          {showReviewed ? 'Hide reviewed' : 'Show reviewed'}
        </Button>
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
        The weekly monitor agent checks the NASBA site for requirement changes. Any changes it
        detects appear below. Review each state and update its requirement data if needed.
      </Typography>
      <Typography variant="body2" sx={{ color: lastRunAt ? 'text.secondary' : 'status.attention.fg', mb: 1.5 }}>
        {lastRunAt
          ? `Monitor last reported ${formatDateTime(lastRunAt)}.`
          : 'No reports received from the monitor agent yet.'}
      </Typography>
      {visible.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          {findings.length === 0 ? 'No requirement changes reported.' : 'All findings reviewed.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visible.map((finding) => (
            <Box
              key={finding.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                borderRadius: '6px',
                border: 1,
                borderColor: 'divider',
                bgcolor: finding.reviewedAt ? 'transparent' : 'status.attention.bg',
              }}
            >
              <Box
                onClick={() => onStateClick(finding.stateCode)}
                sx={{ cursor: 'pointer', flexShrink: 0 }}
                aria-label={`Open ${finding.stateCode} requirement details`}
              >
                <StateBadge stateCode={finding.stateCode} />
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                  {finding.summary ?? 'Change detected'}
                </Typography>
                {finding.changedFields.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {finding.changedFields.map((field) => (
                      <Chip key={field} label={field} size="small" variant="outlined" sx={{ height: 20, fontSize: '11px' }} />
                    ))}
                  </Box>
                )}
                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.5 }}>
                  Detected {formatDateTime(finding.detectedAt)}
                  {finding.parsingModel ? ` · ${finding.parsingModel}` : ''}
                  {finding.reviewedAt
                    ? ` · Reviewed ${formatDateTime(finding.reviewedAt)}${finding.reviewedBy ? ` by ${finding.reviewedBy}` : ''}`
                    : ''}
                  {finding.sourceUrl && (
                    <>
                      {' · '}
                      <Link href={finding.sourceUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '11px' }}>
                        NASBA source
                        <ExternalIcon sx={{ fontSize: 11, ml: 0.25, verticalAlign: 'middle' }} />
                      </Link>
                    </>
                  )}
                </Typography>
              </Box>
              {!finding.reviewedAt && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={reviewBusyId === finding.id}
                  onClick={() => onMarkReviewed(finding.id)}
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Mark reviewed
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

/** Label/value pair for the drill-down field grid. */
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '13px', fontWeight: 500, overflowWrap: 'anywhere' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function TextBlock({ label, text }: { label: string; text: string | null }) {
  if (!text) return null;
  return (
    <Box>
      <Typography sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{text}</Typography>
    </Box>
  );
}

function JsonBlock({ label, json }: { label: string; json: string | null }) {
  const [open, setOpen] = useState(false);
  if (!json) return null;

  let pretty = json;
  try {
    pretty = JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    // Show raw text if it isn't valid JSON
  }

  return (
    <Box>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 0.25 }}
      >
        <Typography sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </Typography>
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        )}
      </Box>
      <Collapse in={open}>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: '6px',
            fontSize: '12px',
            fontFamily: 'monospace',
            overflowX: 'auto',
            maxHeight: 320,
          }}
        >
          {pretty}
        </Box>
      </Collapse>
    </Box>
  );
}

/** ISO datetime -> yyyy-MM-dd for a date input ('' when null). */
function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

/**
 * Build the date payload for a possibly-edited date field. Untouched fields
 * return the original ISO string so time-of-day components (e.g. period-end
 * 23:59:59) survive the full-replace save; edited fields get an explicit UTC
 * time (end-of-day for period end, midnight otherwise).
 */
function buildDatePayload(original: string | null, input: string, endOfDay = false): string | null {
  if (input === toDateInput(original)) return original;
  if (!input) return null;
  return endOfDay ? `${input}T23:59:59Z` : `${input}T00:00:00Z`;
}

function intOrNull(value: string): number | null {
  return value.trim() === '' ? null : Number(value);
}

interface EditFormState {
  generalRequirementHours: string;
  ethicsRequirementHours: string;
  reportingPeriodYears: string;
  minimumHoursPerYear: string;
  reportingPeriodType: string;
  birthMonthAlignment: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  effectiveDate: string;
  expirationDate: string;
  licenseRenewalDate: string;
  dateOfLastRevision: string;
  cpeReportingPeriod: string;
  periodCalculationNotes: string;
  otherSubjectAreaRequirements: string;
  creditLimitationsText: string;
  creditCalculationText: string;
  otherPoliciesText: string;
  allowsReciprocity: string;
  reciprocityEthicsCarveout: string;
  reciprocityNotesText: string;
  carryoverMaxHours: string;
}

/** Non-negative hours in tenths ('' = no verified rule). String check, not float math. */
const CARRYOVER_HOURS_PATTERN = /^\d{1,4}(\.\d)?$/;

function RequirementFieldsEditForm({
  detail,
  busy,
  error,
  onCancel,
  onSave,
}: {
  detail: CpeRequirementImportDetail;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (payload: UpdateCpeRequirementFields) => void;
}) {
  const [form, setForm] = useState<EditFormState>({
    generalRequirementHours: detail.generalRequirementHours?.toString() ?? '',
    ethicsRequirementHours: detail.ethicsRequirementHours?.toString() ?? '',
    reportingPeriodYears: detail.reportingPeriodYears.toString(),
    minimumHoursPerYear: detail.minimumHoursPerYear?.toString() ?? '',
    reportingPeriodType: detail.reportingPeriodType,
    birthMonthAlignment: detail.birthMonthAlignment ?? '',
    currentPeriodStart: toDateInput(detail.currentPeriodStart),
    currentPeriodEnd: toDateInput(detail.currentPeriodEnd),
    effectiveDate: toDateInput(detail.effectiveDate),
    expirationDate: toDateInput(detail.expirationDate),
    licenseRenewalDate: detail.licenseRenewalDate ?? '',
    dateOfLastRevision: toDateInput(detail.dateOfLastRevision),
    cpeReportingPeriod: detail.cpeReportingPeriod ?? '',
    periodCalculationNotes: detail.periodCalculationNotes ?? '',
    otherSubjectAreaRequirements: detail.otherSubjectAreaRequirements ?? '',
    creditLimitationsText: detail.creditLimitationsText ?? '',
    creditCalculationText: detail.creditCalculationText ?? '',
    otherPoliciesText: detail.otherPoliciesText ?? '',
    allowsReciprocity: detail.allowsReciprocity ? 'true' : 'false',
    reciprocityEthicsCarveout: detail.reciprocityEthicsCarveout ? 'true' : 'false',
    reciprocityNotesText: detail.reciprocityNotesText ?? '',
    carryoverMaxHours: detail.carryoverMaxHours?.toString() ?? '',
  });

  const set = (field: keyof EditFormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const periodYears = Number(form.reportingPeriodYears);
  const periodYearsValid = Number.isInteger(periodYears) && periodYears >= 1;

  const carryoverTrimmed = form.carryoverMaxHours.trim();
  const carryoverValid = carryoverTrimmed === '' || CARRYOVER_HOURS_PATTERN.test(carryoverTrimmed);

  const handleSave = () => {
    onSave({
      generalRequirementHours: intOrNull(form.generalRequirementHours),
      ethicsRequirementHours: intOrNull(form.ethicsRequirementHours),
      reportingPeriodYears: periodYears,
      minimumHoursPerYear: intOrNull(form.minimumHoursPerYear),
      reportingPeriodType: form.reportingPeriodType,
      birthMonthAlignment: form.birthMonthAlignment || null,
      currentPeriodStart: buildDatePayload(detail.currentPeriodStart, form.currentPeriodStart),
      currentPeriodEnd: buildDatePayload(detail.currentPeriodEnd, form.currentPeriodEnd, true),
      effectiveDate: buildDatePayload(detail.effectiveDate, form.effectiveDate),
      expirationDate: buildDatePayload(detail.expirationDate, form.expirationDate),
      licenseRenewalDate: form.licenseRenewalDate.trim() || null,
      dateOfLastRevision: buildDatePayload(detail.dateOfLastRevision, form.dateOfLastRevision),
      cpeReportingPeriod: form.cpeReportingPeriod.trim() || null,
      periodCalculationNotes: form.periodCalculationNotes.trim() || null,
      otherSubjectAreaRequirements: form.otherSubjectAreaRequirements.trim() || null,
      creditLimitationsText: form.creditLimitationsText.trim() || null,
      creditCalculationText: form.creditCalculationText.trim() || null,
      otherPoliciesText: form.otherPoliciesText.trim() || null,
      // Always sent as concrete values; null would mean "leave unchanged" server-side.
      allowsReciprocity: form.allowsReciprocity === 'true',
      reciprocityEthicsCarveout: form.reciprocityEthicsCarveout === 'true',
      reciprocityNotesText: form.reciprocityNotesText.trim(),
      carryoverMaxHours: carryoverTrimmed,
    });
  };

  const dateField = (label: string, field: keyof EditFormState) => (
    <TextField
      label={label}
      type="date"
      size="small"
      value={form[field]}
      onChange={set(field)}
      slotProps={{ inputLabel: { shrink: true } }}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <SectionHeading>Edit Requirement Field Values</SectionHeading>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <TextField label="General Hours" type="number" size="small" value={form.generalRequirementHours} onChange={set('generalRequirementHours')} />
          <TextField label="Ethics Hours" type="number" size="small" value={form.ethicsRequirementHours} onChange={set('ethicsRequirementHours')} />
          <TextField
            label="Reporting Period (Years)"
            type="number"
            size="small"
            required
            value={form.reportingPeriodYears}
            onChange={set('reportingPeriodYears')}
            error={!periodYearsValid}
            helperText={periodYearsValid ? undefined : 'Whole number, at least 1'}
          />
          <TextField label="Minimum Hours / Year" type="number" size="small" value={form.minimumHoursPerYear} onChange={set('minimumHoursPerYear')} />
          <TextField label="Period Type" select size="small" value={form.reportingPeriodType} onChange={set('reportingPeriodType')}>
            <MenuItem value="CalendarBased">Calendar Based</MenuItem>
            <MenuItem value="BirthdateBased">Birthdate Based</MenuItem>
            <MenuItem value="LicenseDateBased">License Date Based</MenuItem>
          </TextField>
          <TextField label="Birth Month Alignment" select size="small" value={form.birthMonthAlignment} onChange={set('birthMonthAlignment')}>
            <MenuItem value="">None</MenuItem>
            <MenuItem value="odd">Odd years</MenuItem>
            <MenuItem value="even">Even years</MenuItem>
            <MenuItem value="matches_birth_year">Matches birth year</MenuItem>
          </TextField>
          {dateField('Current Period Start', 'currentPeriodStart')}
          {dateField('Current Period End', 'currentPeriodEnd')}
          {dateField('Effective Date', 'effectiveDate')}
          {dateField('Expiration Date', 'expirationDate')}
          <TextField label="License Renewal" size="small" value={form.licenseRenewalDate} onChange={set('licenseRenewalDate')} />
          {dateField('Date of Last Revision', 'dateOfLastRevision')}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
          <TextField label="CPE Reporting Period" size="small" multiline minRows={2} value={form.cpeReportingPeriod} onChange={set('cpeReportingPeriod')} />
          <TextField label="Period Calculation Notes" size="small" multiline minRows={2} value={form.periodCalculationNotes} onChange={set('periodCalculationNotes')} />
        </Box>
      </Box>

      <Box>
        <SectionHeading>Reciprocity & Carryover</SectionHeading>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <TextField label="Allows Reciprocity" select size="small" value={form.allowsReciprocity} onChange={set('allowsReciprocity')}>
            <MenuItem value="false">No</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
          </TextField>
          <TextField label="Ethics Carve-out" select size="small" value={form.reciprocityEthicsCarveout} onChange={set('reciprocityEthicsCarveout')}>
            <MenuItem value="false">No</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
          </TextField>
          <TextField
            label="Carryover Max Hours"
            size="small"
            value={form.carryoverMaxHours}
            onChange={set('carryoverMaxHours')}
            error={!carryoverValid}
            helperText={carryoverValid ? 'Blank = no verified rule' : 'Tenths allowed (e.g. 20.5)'}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
          <TextField label="Reciprocity Notes" size="small" multiline minRows={2} value={form.reciprocityNotesText} onChange={set('reciprocityNotesText')} />
        </Box>
      </Box>

      <Box>
        <SectionHeading>Supplementary Information</SectionHeading>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField label="Other Subject Area Requirements" size="small" multiline minRows={3} value={form.otherSubjectAreaRequirements} onChange={set('otherSubjectAreaRequirements')} />
          <TextField label="Credit Limitations" size="small" multiline minRows={3} value={form.creditLimitationsText} onChange={set('creditLimitationsText')} />
          <TextField label="Credit Calculation" size="small" multiline minRows={3} value={form.creditCalculationText} onChange={set('creditCalculationText')} />
          <TextField label="Other Policies" size="small" multiline minRows={3} value={form.otherPoliciesText} onChange={set('otherPoliciesText')} />
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button size="small" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={busy || !periodYearsValid || !carryoverValid}>
          {busy ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
}

function DetailContent({ detail, onEdit }: { detail: CpeRequirementImportDetail; onEdit: () => void }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Import metadata */}
      <Box>
        <SectionHeading>Import Status</SectionHeading>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <Field label="Last Scraped" value={formatDateTime(detail.lastScrapedAt)} />
          <Field label="NASBA Source Updated" value={formatDate(detail.sourceLastUpdated)} />
          <Field label="Last AI Parse" value={formatDateTime(detail.parsedDetail?.lastParsedAt ?? null)} />
          <Field label="Parsing Model" value={detail.parsedDetail?.parsingModel ?? '—'} />
          <Field label="Record Created" value={formatDateTime(detail.createdAt)} />
          <Field label="Record Updated" value={formatDateTime(detail.updatedAt)} />
        </Box>
        {(detail.nasbaUrl || detail.stateBoardUrl) && (
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
            {detail.nasbaUrl && (
              <Link
                href={detail.nasbaUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                NASBA Source <ExternalIcon sx={{ fontSize: 14 }} />
              </Link>
            )}
            {detail.stateBoardUrl && (
              <Link
                href={detail.stateBoardUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                State Board <ExternalIcon sx={{ fontSize: 14 }} />
              </Link>
            )}
          </Box>
        )}
      </Box>

      <Divider />

      {/* Core requirement field values */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <SectionHeading>Requirement Field Values</SectionHeading>
          <Button size="small" startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={onEdit}>
            Edit
          </Button>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <Field label="General Hours" value={detail.generalRequirementHours ?? '—'} />
          <Field label="Ethics Hours" value={detail.ethicsRequirementHours ?? '—'} />
          <Field label="Reporting Period (Years)" value={detail.reportingPeriodYears} />
          <Field label="Minimum Hours / Year" value={detail.minimumHoursPerYear ?? '—'} />
          <Field label="Period Type" value={detail.reportingPeriodType} />
          <Field label="Birth Month Alignment" value={detail.birthMonthAlignment ?? '—'} />
          <Field label="Current Period Start" value={formatDate(detail.currentPeriodStart)} />
          <Field label="Current Period End" value={formatDate(detail.currentPeriodEnd)} />
          <Field label="Effective Date" value={formatDate(detail.effectiveDate)} />
          <Field label="Expiration Date" value={formatDate(detail.expirationDate)} />
          <Field label="License Renewal" value={detail.licenseRenewalDate ?? '—'} />
          <Field label="Date of Last Revision" value={formatDate(detail.dateOfLastRevision)} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
          <TextBlock label="CPE Reporting Period" text={detail.cpeReportingPeriod} />
          <TextBlock label="Period Calculation Notes" text={detail.periodCalculationNotes} />
        </Box>
      </Box>

      <Divider />

      {/* Engine-driving reciprocity + carryover values; always shown so "No"/"—" is visible */}
      <Box>
        <SectionHeading>Reciprocity & Carryover</SectionHeading>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <Field label="Allows Reciprocity" value={detail.allowsReciprocity ? 'Yes' : 'No'} />
          <Field label="Ethics Carve-out" value={detail.reciprocityEthicsCarveout ? 'Yes' : 'No'} />
          <Field label="Carryover Max Hours" value={detail.carryoverMaxHours ?? '—'} />
        </Box>
        {detail.reciprocityNotesText && (
          <Box sx={{ mt: 1.5 }}>
            <TextBlock label="Reciprocity Notes" text={detail.reciprocityNotesText} />
          </Box>
        )}
      </Box>

      {/* Flexible requirement model — what compliance calculations actually consume */}
      {detail.requirementCategories.length > 0 && (
        <>
          <Divider />
          <Box>
            <SectionHeading>Requirement Categories</SectionHeading>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {detail.requirementCategories.map((cat) => (
                <Box key={cat.id}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                    {cat.categoryName}
                    {cat.isConditional && ' (conditional)'}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    {cat.categoryType}
                    {cat.targetKind !== 'unmapped' && ` · tracked as ${cat.targetKind.replace('_', '-')}`}
                  </Typography>
                  {cat.constraints.map((con) => (
                    <Typography key={con.id} sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      • {formatConstraint(con)}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* Supplementary text sections */}
      {(detail.otherSubjectAreaRequirements
        || detail.creditLimitationsText
        || detail.creditCalculationText
        || detail.otherPoliciesText) && (
        <>
          <Divider />
          <Box>
            <SectionHeading>Supplementary Information</SectionHeading>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextBlock label="Other Subject Area Requirements" text={detail.otherSubjectAreaRequirements} />
              <TextBlock label="Credit Limitations" text={detail.creditLimitationsText} />
              <TextBlock label="Credit Calculation" text={detail.creditCalculationText} />
              <TextBlock label="Other Policies" text={detail.otherPoliciesText} />
            </Box>
          </Box>
        </>
      )}

      {/* Related collections */}
      {detail.subjectRequirements.length > 0 && (
        <>
          <Divider />
          <Box>
            <SectionHeading>Subject Requirements</SectionHeading>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 0 }}>Subject Area</TableCell>
                  <TableCell align="right" sx={{ pr: 0 }}>Hours</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.subjectRequirements.map((sr) => (
                  <TableRow key={sr.id} sx={{ '& td': { fontSize: '13px' } }}>
                    <TableCell sx={{ pl: 0 }}>
                      {sr.subjectArea}
                      {sr.notes && (
                        <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>{sr.notes}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {sr.requiredHours}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}

      {detail.creditLimitations.length > 0 && (
        <Box>
          <SectionHeading>Credit Limitations</SectionHeading>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {detail.creditLimitations.map((cl) => (
              <Box key={cl.id}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                  {cl.limitationType}
                  {cl.maxHours != null && ` · max ${cl.maxHours} hrs`}
                  {cl.maxPercentage != null && ` · max ${cl.maxPercentage}%`}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{cl.limitationDescription}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {detail.calculationRules.length > 0 && (
        <Box>
          <SectionHeading>Credit Calculation Rules</SectionHeading>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {detail.calculationRules.map((cr) => (
              <Box key={cr.id}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{cr.activityType}</Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{cr.calculationRule}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {detail.policies.length > 0 && (
        <Box>
          <SectionHeading>Policies</SectionHeading>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {detail.policies.map((p) => (
              <Box key={p.id}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{p.policyType}</Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{p.policyDescription}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* AI-parsed detail record */}
      {detail.parsedDetail && (
        <>
          <Divider />
          <Box>
            <SectionHeading>AI-Parsed Detail</SectionHeading>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
              <Field label="Total Hours Required" value={detail.parsedDetail.totalHoursRequired ?? '—'} />
              <Field label="Ethics Hours Required" value={detail.parsedDetail.ethicsHoursRequired ?? '—'} />
              <Field label="Reporting Period (Months)" value={detail.parsedDetail.reportingPeriodMonths ?? '—'} />
              <Field label="Detail Updated" value={formatDateTime(detail.parsedDetail.updatedAt)} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextBlock label="Carryover Rules" text={detail.parsedDetail.carryoverRules} />
              <TextBlock label="Notes" text={detail.parsedDetail.notes} />
              <JsonBlock label="Requirements JSON" json={detail.parsedDetail.requirementsJson} />
            </Box>
          </Box>
        </>
      )}

      {/* Last recorded change */}
      {detail.lastChange && (
        <>
          <Divider />
          <Box>
            <SectionHeading>Last Recorded Change</SectionHeading>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
              <Field label="Change Type" value={detail.lastChange.changeType} />
              <Field label="Changed At" value={formatDateTime(detail.lastChange.changeAt)} />
              <Field label="Processed By" value={detail.lastChange.processedBy ?? '—'} />
              <Field label="Parsing Model" value={detail.lastChange.parsingModel ?? '—'} />
            </Box>
            {detail.lastChange.changedFields.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {detail.lastChange.changedFields.map((f) => (
                  <Chip key={f} label={f} size="small" variant="outlined" sx={{ height: 22 }} />
                ))}
              </Box>
            )}
            <TextBlock label="Notes" text={detail.lastChange.notes} />
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * System admin visibility into the CPE requirements import pipeline:
 * per-jurisdiction last successful scrape/parse timestamps with drill-down
 * into the imported field values.
 */
export default function CpeRequirementImports() {
  const [statuses, setStatuses] = useState<CpeRequirementImportStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [selectedState, setSelectedState] = useState<CpeRequirementImportStatus | null>(null);
  const [detail, setDetail] = useState<CpeRequirementImportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [findings, setFindings] = useState<MonitorFinding[]>([]);
  const [monitorLastRunAt, setMonitorLastRunAt] = useState<string | null>(null);
  const [showReviewed, setShowReviewed] = useState(false);
  const [reviewBusyId, setReviewBusyId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [statusRes, findingsRes, monitorStatusRes] = await Promise.allSettled([
          cpeRequirementImportService.getImportStatus(),
          cpeRequirementImportService.getMonitorFindings(false),
          cpeRequirementImportService.getMonitorStatus(),
        ]);
        if (cancelled) return;
        if (statusRes.status === 'fulfilled') {
          setStatuses(statusRes.value);
        } else {
          setError(
            statusRes.reason instanceof Error ? statusRes.reason.message : 'Failed to load import status',
          );
        }
        // Monitor data is secondary content: a failure here shouldn't block the page.
        if (findingsRes.status === 'fulfilled') {
          setFindings(findingsRes.value);
        }
        if (monitorStatusRes.status === 'fulfilled') {
          setMonitorLastRunAt(monitorStatusRes.value.lastRunAt);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleMarkReviewed = async (id: string) => {
    if (reviewBusyId) return;
    setReviewBusyId(id);
    setReviewError(null);
    try {
      const updated = await cpeRequirementImportService.markMonitorFindingReviewed(id);
      setFindings((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Failed to mark finding reviewed');
    } finally {
      setReviewBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return statuses;
    return statuses.filter(
      (s) => s.stateName.toLowerCase().includes(term) || s.stateCode.toLowerCase().includes(term),
    );
  }, [statuses, search]);

  // Guards against out-of-order detail responses: only the most recent request
  // (or none, after closing the drawer) may write detail state.
  const detailRequestRef = useRef(0);

  const handleToggleVerification = async () => {
    if (!selectedState || verifyBusy) return;
    const markVerified = !selectedState.verifiedAt;
    setVerifyBusy(true);
    setVerifyError(null);
    try {
      const v = await cpeRequirementImportService.setVerification(
        selectedState.stateCode,
        markVerified,
      );
      const apply = <T extends { verifiedAt: string | null; verifiedBy: string | null }>(
        item: T,
      ): T => ({ ...item, verifiedAt: v.verifiedAt, verifiedBy: v.verifiedBy });
      setStatuses((prev) => prev.map((s) => (s.stateCode === v.stateCode ? apply(s) : s)));
      setSelectedState((prev) => (prev && prev.stateCode === v.stateCode ? apply(prev) : prev));
      setDetail((prev) => (prev && prev.stateCode === v.stateCode ? apply(prev) : prev));
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Failed to update verification status');
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleSaveEdit = async (payload: UpdateCpeRequirementFields) => {
    if (!selectedState || saveBusy) return;
    setSaveBusy(true);
    setSaveError(null);
    try {
      const updated = await cpeRequirementImportService.updateImportDetail(
        selectedState.stateCode,
        payload,
      );
      setDetail(updated);
      setEditing(false);
      // Reflect the new change signal in the list row and drawer header so the
      // verification staleness chip updates without a full reload.
      const change = updated.lastChange;
      if (change) {
        const apply = <T extends { lastChangeAt: string | null; lastChangeType: string | null }>(
          item: T,
        ): T => ({ ...item, lastChangeAt: change.changeAt, lastChangeType: change.changeType });
        setStatuses((prev) => prev.map((s) => (s.stateCode === updated.stateCode ? apply(s) : s)));
        setSelectedState((prev) => (prev && prev.stateCode === updated.stateCode ? apply(prev) : prev));
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save requirement fields');
    } finally {
      setSaveBusy(false);
    }
  };

  const handleRowClick = async (row: CpeRequirementImportStatus) => {
    const requestId = ++detailRequestRef.current;
    setSelectedState(row);
    setDetail(null);
    setDetailError(null);
    setVerifyError(null);
    setEditing(false);
    setSaveError(null);
    setDetailLoading(true);
    try {
      const data = await cpeRequirementImportService.getImportDetail(row.stateCode);
      if (detailRequestRef.current !== requestId) return;
      setDetail(data);
    } catch (err) {
      if (detailRequestRef.current !== requestId) return;
      setDetailError(err instanceof Error ? err.message : 'Failed to load import detail');
    } finally {
      if (detailRequestRef.current === requestId) setDetailLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    detailRequestRef.current++; // invalidate any in-flight detail request
    setSelectedState(null);
    setDetail(null);
    setDetailError(null);
    setVerifyError(null);
    setEditing(false);
    setSaveError(null);
    setDetailLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {reviewError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReviewError(null)}>
          {reviewError}
        </Alert>
      )}
      <MonitorFindingsSection
        findings={findings}
        lastRunAt={monitorLastRunAt}
        showReviewed={showReviewed}
        onToggleShowReviewed={() => setShowReviewed((v) => !v)}
        reviewBusyId={reviewBusyId}
        onMarkReviewed={handleMarkReviewed}
        onStateClick={(stateCode) => {
          const row = statuses.find((s) => s.stateCode === stateCode);
          if (row) handleRowClick(row);
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Last successful NASBA import per jurisdiction. Select a row to inspect the imported field values.
        </Typography>
        <TextField
          size="small"
          placeholder="Search jurisdictions"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { 'aria-label': 'Search jurisdictions' },
          }}
          sx={{ width: { xs: '100%', sm: 260 } }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }} elevation={0}>
        <Table size="small" aria-label="CPE requirements import status by jurisdiction">
          <TableHead>
            <TableRow>
              <TableCell>Jurisdiction</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Scraped</TableCell>
              <TableCell>Source Updated</TableCell>
              <TableCell>Last AI Parse</TableCell>
              <TableCell>Last Change</TableCell>
              <TableCell>Verified</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', py: 2 }}>
                    No jurisdictions match your search.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow
                  key={row.stateCode}
                  hover
                  onClick={() => handleRowClick(row)}
                  sx={{ cursor: 'pointer', '& td': { fontSize: '13px' } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StateBadge stateCode={row.stateCode} />
                      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{row.stateName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <ImportStandingChip standing={getImportStanding(row)} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.lastScrapedAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.sourceLastUpdated)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(row.lastParsedAt)}
                    {row.parsingModel && (
                      <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>{row.parsingModel}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.lastChangeAt ? (
                      <>
                        {formatDateTime(row.lastChangeAt)}
                        {row.lastChangeType && (
                          <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>{row.lastChangeType}</Typography>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <VerificationCellContent row={row} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drill-down drawer */}
      <Drawer
        anchor="right"
        open={selectedState != null}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}
      >
        {selectedState && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5, pb: 2 }}>
              <StateBadge stateCode={selectedState.stateCode} />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {selectedState.stateName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Imported requirement values
                </Typography>
              </Box>
              <IconButton onClick={handleCloseDrawer} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />

            {/* Scrollable content */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5 }}>
              {/* Human verification */}
              <Box
                sx={{
                  mb: 2.5,
                  p: 1.5,
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {selectedState.verifiedAt ? (
                    <VerificationCellContent row={selectedState} />
                  ) : (
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                      Not human-verified yet
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleToggleVerification}
                  disabled={verifyBusy}
                >
                  {selectedState.verifiedAt ? 'Clear Verification' : 'Mark as Verified'}
                </Button>
              </Box>
              {verifyError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {verifyError}
                </Alert>
              )}
              {detailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {detailError && <Alert severity="error">{detailError}</Alert>}
              {detail && !editing && (
                <DetailContent
                  detail={detail}
                  onEdit={() => {
                    setSaveError(null);
                    setEditing(true);
                  }}
                />
              )}
              {detail && editing && (
                <RequirementFieldsEditForm
                  detail={detail}
                  busy={saveBusy}
                  error={saveError}
                  onCancel={() => setEditing(false)}
                  onSave={handleSaveEdit}
                />
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
